import { Button, Paper, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { date, daysBetween, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { StatusChip } from '../../ui/primitives';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function RemittancesPanel() {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const rows = useMemo(
    () =>
      state.remittances.map((remittance) => {
        const entries = scope.ledger.filter(
          (entry) =>
            entry.type === 'WITHHOLDING' && entry.effectiveDate.startsWith(remittance.month),
        );
        const tfn = entries
          .filter((entry) => scope.holderById.get(entry.holderId)?.residency === 'AU')
          .reduce((sum, entry) => sum + entry.amount, 0);
        const total = entries.reduce((sum, entry) => sum + entry.amount, 0);
        return { remittance, tfn, nr: total - tfn, total };
      }),
    [state.remittances, scope],
  );
  if (scope.tenant.id !== 't-fides') return null;
  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ px: 2.5, pt: 2 }}>
        Remittances to the ATO
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 1 }}>
        Amounts withheld in a month are paid to the ATO by the 21st of the next month (illustrative
        cycle).
      </Typography>
      <DataTable
        rows={rows}
        rowKey={(row) => row.remittance.id}
        dense
        columns={[
          {
            key: 'month',
            label: 'Month withheld',
            render: (row) =>
              `${MONTHS[Number(row.remittance.month.slice(5, 7)) - 1]} ${row.remittance.month.slice(0, 4)}`,
          },
          { key: 'tfn', label: 'TFN', align: 'right', render: (row) => money(row.tfn, true) },
          {
            key: 'nr',
            label: 'Non-resident',
            align: 'right',
            render: (row) => money(row.nr, true),
          },
          {
            key: 'total',
            label: 'Total',
            align: 'right',
            render: (row) => <strong>{money(row.total, true)}</strong>,
          },
          { key: 'due', label: 'Due', render: (row) => date(row.remittance.dueDate) },
          {
            key: 'status',
            label: 'Status',
            render: (row) =>
              row.remittance.remittedAt ? (
                <StatusChip status="COMPLETED" label={`Paid ${date(row.remittance.remittedAt)}`} />
              ) : (
                <StatusChip
                  status={
                    daysBetween(DEMO_TODAY, row.remittance.dueDate) < 0 ? 'OVERDUE' : 'PENDING'
                  }
                  label={
                    daysBetween(DEMO_TODAY, row.remittance.dueDate) < 0
                      ? 'Overdue'
                      : `Due in ${daysBetween(DEMO_TODAY, row.remittance.dueDate)}d`
                  }
                />
              ),
          },
          {
            key: 'action',
            label: '',
            align: 'right',
            render: (row) =>
              !row.remittance.remittedAt && row.remittance.month < DEMO_TODAY.slice(0, 7) ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    dispatch({
                      type: 'REMITTANCE_RECORD',
                      id: row.remittance.id,
                      reference: `IAS-${row.remittance.month}`,
                    });
                    toast('Remittance recorded');
                  }}
                >
                  Record payment
                </Button>
              ) : null,
          },
        ]}
      />
    </Paper>
  );
}
